import React from 'react';
import { observer } from 'mobx-react';
import styled, { css } from 'styled-components';
import { Typography } from 'src/typography';
import { MemoizedPlaidEmbeddedSearch } from 'src/payments/components';
import { PlaidLinkCustomizationNames } from 'src/payments/constants';
import { CircleBankLogo } from 'src/payments/components/circle-bank-logo';
import { BankActions } from 'src/payments/components/bank-actions';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PayByBankSignup } from '../pay-by-bank-signup';
import { usePayByBankDetails } from './use-pay-by-bank-details';

const { EMBEDDED_SEARCH_ONLINE_POPUP, EMBEDDED_SEARCH_ONLINE } = PlaidLinkCustomizationNames;

export const PayByBankDetails = observer(
  (): JSX.Element => {
    const {
      bankDetails,
      bankNeedsRelinking,
      showBankInformation,
      loggedOutPBBSignupEnabled,
      loggedInPBBSignupEnabled,
      hasOtherOrderTypesWithPBBEnabled,
      onEditBank,
      onRelinkBank,
      openTermsModal,
    } = usePayByBankDetails();

    const flags = useFlags();

    const { bankLogo, lastDigits, bankName = '' } = bankDetails ?? {};
    const showPBBSignup =
      !showBankInformation &&
      !loggedInPBBSignupEnabled &&
      (loggedOutPBBSignupEnabled || hasOtherOrderTypesWithPBBEnabled);

    const embeddedSearchPopupEnabled = flags['fintech.platform.plaid-embedded-search-popup'] ?? false;

    return (
      <>
        {/* User is logged in and signed up for Pay By Bank */}
        {showBankInformation && (
          <BankCardContainer>
            <BankLogoAndDetails>
              <CircleBankLogo
                altText={`${bankName} Logo`}
                logo={bankLogo}
                circleDiameter={32}
                iconHeight={26}
                iconWidth={26}
              />
              <BankNameAndDigits>
                <BankName weight='semi' size='medium'>
                  {bankName}
                </BankName>
                <BankDigits size='small'>ending in ‧‧‧‧ {lastDigits}</BankDigits>
              </BankNameAndDigits>
            </BankLogoAndDetails>
            <BankActions
              bankNeedsRelinking={bankNeedsRelinking}
              editBankAccount={onEditBank}
              relinkBankAccount={onRelinkBank}
            />
          </BankCardContainer>
        )}
        {loggedInPBBSignupEnabled && (
          <PlaidSearchContainer data-testid='plaid-embedded-search-container'>
            <PaymentTitle weight='semi' size='large'>
              Select your bank to pay instantly:
            </PaymentTitle>
            <MemoizedPlaidEmbeddedSearch
              handleEmbeddedDutchiePayLink={() => void openTermsModal()}
              linkCustomizationName={embeddedSearchPopupEnabled ? EMBEDDED_SEARCH_ONLINE_POPUP : EMBEDDED_SEARCH_ONLINE}
              width='calc(100% + 12px)'
            />
          </PlaidSearchContainer>
        )}
        {/* User is not enrolled in Dutchie Pay */}
        {showPBBSignup && <PayByBankSignup />}
      </>
    );
  }
);

const PlaidSearchContainer = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spaces[4]};
    align-self: stretch;
    background-color: transparent;
  `}
`;

const PaymentTitle = styled(Typography.Body)`
  ${({ theme: { colors } }) => css`
    color: ${colors.primaryBlack};
    line-height: 20px;
    align-self: stretch;
  `}
`;

const BankCardContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const BankLogoAndDetails = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spaces } }) => spaces[2]};
`;

const BankNameAndDigits = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const BankName = styled(Typography.Body)`
  color: #393d40;
  line-height: 20px;
`;

const BankDigits = styled(Typography.Body)`
  color: #4b5257;
  line-height: 120%;
`;
